import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Message } from 'primereact/message';
import { Skeleton } from 'primereact/skeleton';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Paginator } from 'primereact/paginator';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import invoiceService from '../services/invoiceService';

const STATUS_OPTIONS = [
    { label: 'All statuses', value: null },
    { label: 'Paid', value: 'paid' },
    { label: 'Pending', value: 'pending' },
    { label: 'Failed', value: 'failed' },
];

// Mobile card for the invoice list — same pattern as ServiceMobileRow
const InvoiceMobileRow = ({ invoice }) => (
    <div className="flex justify-content-between align-items-center gap-2 w-full">
        <span className="flex-1 min-w-0 font-medium text-sm text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">
            {invoice.invoice_number || '—'}
        </span>
        <Tag
            value={invoice.status?.toUpperCase() || '—'}
            severity={invoiceService.getStatusSeverity(invoice.status)}
            className="text-xs flex-shrink-0"
        />
    </div>
);

const BillingHistory = () => {
    // Data
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination + sort
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [sortField, setSortField] = useState('invoice_date');
    const [sortOrder, setSortOrder] = useState(-1);

    // Filters
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const [statusFilter, setStatusFilter] = useState(null);

    // Per-row download state
    const [downloadingInvoices, setDownloadingInvoices] = useState({});

    // Mobile accordion
    const [mobileActiveIndex, setMobileActiveIndex] = useState(null);

    // Refresh nonce — forces re-fetch even when other deps don't change
    const [refreshNonce, setRefreshNonce] = useState(0);

    // Debounce search (P13)
    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedSearch(search.trim());
            setFirst(0);
        }, 500);
        return () => clearTimeout(t);
    }, [search]);

    // Single fetch effect with primitive deps (P14)
    useEffect(() => {
        let cancelled = false;
        const loadInvoices = async () => {
            setLoading(true);
            setError(null);
            try {
                const page = Math.floor(first / rows) + 1;
                const params = {
                    page,
                    limit: rows,
                    sort_field: sortField,
                    sort_order: sortOrder === 1 ? 'asc' : 'desc',
                };
                if (debouncedSearch) params.search = debouncedSearch;
                if (statusFilter) params.status = statusFilter;
                if (dateFrom) params.date_from = dateFrom.toISOString().split('T')[0];
                if (dateTo) params.date_to = dateTo.toISOString().split('T')[0];

                const response = await invoiceService.getInvoices(params);
                if (cancelled) return;
                if (response.data) {
                    setInvoices(response.data);
                    setTotalRecords(response.total || 0);
                } else {
                    setInvoices([]);
                    setTotalRecords(0);
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Failed to load invoices:', err?.message || 'Unknown error');
                    setError('Failed to load billing history. Please try again.');
                    setInvoices([]);
                    setTotalRecords(0);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        loadInvoices();
        return () => { cancelled = true; };
    }, [first, rows, sortField, sortOrder, debouncedSearch, statusFilter, dateFrom, dateTo, refreshNonce]);

    // Reset mobile expanded row when result set changes (P17)
    useEffect(() => {
        setMobileActiveIndex(null);
    }, [first, debouncedSearch, statusFilter, dateFrom, dateTo]);

    const clearFilters = () => {
        setSearch('');
        setDateFrom(null);
        setDateTo(null);
        setStatusFilter(null);
        setFirst(0);
    };

    const refresh = () => setRefreshNonce((n) => n + 1);

    const hasFilters = search || dateFrom || dateTo || statusFilter !== null;

    const handleDownloadInvoice = useCallback(async (invoice) => {
        setDownloadingInvoices((prev) => ({ ...prev, [invoice.id]: true }));
        try {
            await invoiceService.downloadInvoice(invoice.id, invoice.invoice_number);
        } catch (err) {
            console.error('Download error:', err?.message || 'Unknown error');
        } finally {
            setDownloadingInvoices((prev) => ({ ...prev, [invoice.id]: false }));
        }
    }, []);

    // Desktop DataTable column templates
    const invoiceNumberBodyTemplate = (rowData) => (
        <div className="flex flex-column">
            <span className="font-semibold text-sm text-primary">{rowData.invoice_number}</span>
            <span className="text-xs text-500">{invoiceService.formatDate(rowData.invoice_date)}</span>
        </div>
    );

    const descriptionBodyTemplate = (rowData) => (
        <div className="flex flex-column">
            <span className="text-sm text-900 font-medium">{invoiceService.getInvoiceDescription(rowData)}</span>
            {rowData.order?.credits && (
                <span className="text-xs text-500">+{rowData.order.credits} credits added</span>
            )}
        </div>
    );

    const amountBodyTemplate = (rowData) => (
        <div className="flex flex-column">
            <span className="font-semibold text-sm text-900">{invoiceService.formatCurrency(rowData.total_amount)}</span>
            <span className="text-xs text-500">Incl. taxes</span>
        </div>
    );

    const statusBodyTemplate = (rowData) => (
        <Tag
            value={rowData.status?.toUpperCase() || '—'}
            severity={invoiceService.getStatusSeverity(rowData.status)}
        />
    );

    const actionsBodyTemplate = (rowData) => (
        <Button
            icon="pi pi-download"
            severity="info"
            outlined
            size="small"
            aria-label="Download invoice"
            tooltip="Download invoice"
            tooltipOptions={{ position: 'left' }}
            loading={!!downloadingInvoices[rowData.id]}
            disabled={!!downloadingInvoices[rowData.id]}
            onClick={() => handleDownloadInvoice(rowData)}
        />
    );

    return (
        <div className="grid">
            <div className="col-12">
                {/* Page header */}
                <div className="mb-4">
                    <h1 className="text-xl font-semibold text-900 m-0 mb-1">Billing History</h1>
                    <p className="text-600 text-sm m-0">View and download your invoices and payment receipts</p>
                </div>

                {error && <Message severity="error" text={error} className="w-full mb-3" />}

                <Card>
                    {/* Filter bar — tinted, inside the same Card as the data list (P7) */}
                    <div className="surface-ground p-3 border-round mb-3">
                        <div className="grid">
                            <div className="col-12 md:col">
                                <IconField iconPosition="left" className="w-full">
                                    <InputIcon className="pi pi-search" />
                                    <InputText
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder="Search invoices"
                                        maxLength={100}
                                        className="w-full p-inputtext-sm"
                                    />
                                </IconField>
                            </div>
                            <div className="col-6 md:col">
                                <Calendar
                                    value={dateFrom}
                                    onChange={(e) => { setDateFrom(e.value); setFirst(0); }}
                                    placeholder="From date"
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                    iconDisplay="input"
                                    maxDate={dateTo || new Date()}
                                    className="w-full p-inputtext-sm"
                                />
                            </div>
                            <div className="col-6 md:col">
                                <Calendar
                                    value={dateTo}
                                    onChange={(e) => { setDateTo(e.value); setFirst(0); }}
                                    placeholder="To date"
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                    iconDisplay="input"
                                    minDate={dateFrom}
                                    maxDate={new Date()}
                                    className="w-full p-inputtext-sm"
                                />
                            </div>
                            <div className="col-12 md:col">
                                <Dropdown
                                    value={statusFilter}
                                    options={STATUS_OPTIONS}
                                    onChange={(e) => { setStatusFilter(e.value); setFirst(0); }}
                                    placeholder="All statuses"
                                    showClear
                                    className="w-full p-inputtext-sm"
                                />
                            </div>
                            <div className="col-12 md:col flex gap-2">
                                <Button
                                    label="Clear"
                                    icon="pi pi-filter-slash"
                                    severity="secondary"
                                    outlined
                                    size="small"
                                    className="flex-1 py-1 md:py-2"
                                    onClick={clearFilters}
                                    disabled={!hasFilters}
                                />
                                <Button
                                    label="Refresh"
                                    icon="pi pi-refresh"
                                    size="small"
                                    className="flex-1 py-1 md:py-2"
                                    onClick={refresh}
                                    loading={loading}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Mobile (< md) — Accordion cards */}
                    <div className="block md:hidden">
                    {loading && (
                        <div className="flex flex-column gap-2">
                            <Skeleton height="70px" />
                            <Skeleton height="70px" />
                            <Skeleton height="70px" />
                        </div>
                    )}
                    {!loading && invoices.length === 0 && (
                        <div className="flex flex-column align-items-center py-5">
                            <i className="pi pi-inbox pi-2x text-400 mb-3" aria-hidden="true" />
                            <span className="text-600">No invoices found</span>
                        </div>
                    )}
                    {!loading && invoices.length > 0 && (
                        <>
                            <Accordion
                                activeIndex={mobileActiveIndex}
                                onTabChange={(e) => setMobileActiveIndex(e.index)}
                            >
                                {invoices.map((inv, idx) => (
                                    <AccordionTab
                                        key={`invoice-${inv.id || idx}`}
                                        header={<InvoiceMobileRow invoice={inv} />}
                                    >
                                        <div className="flex flex-column gap-2 text-xs">
                                            <div className="flex align-items-center gap-2">
                                                <i className="pi pi-calendar text-500 w-1rem" aria-hidden="true" />
                                                <span className="text-500 font-medium w-6rem">Date</span>
                                                <span className="text-700">{invoiceService.formatDate(inv.invoice_date)}</span>
                                            </div>
                                            <div className="flex align-items-start gap-2">
                                                <i className="pi pi-file text-500 w-1rem mt-1" aria-hidden="true" />
                                                <span className="text-500 font-medium w-6rem mt-1">Description</span>
                                                <span className="text-700 line-height-3">{invoiceService.getInvoiceDescription(inv)}</span>
                                            </div>
                                            {inv.order?.credits && (
                                                <div className="flex align-items-center gap-2">
                                                    <i className="pi pi-plus-circle text-green-600 w-1rem" aria-hidden="true" />
                                                    <span className="text-500 font-medium w-6rem">Credits</span>
                                                    <span className="text-700">+{inv.order.credits}</span>
                                                </div>
                                            )}
                                            <div className="flex align-items-center gap-2">
                                                <i className="pi pi-wallet text-500 w-1rem" aria-hidden="true" />
                                                <span className="text-500 font-medium w-6rem">Amount</span>
                                                <span className="text-900 font-semibold">{invoiceService.formatCurrency(inv.total_amount)}</span>
                                                <span className="text-500">(incl. taxes)</span>
                                            </div>
                                            <div className="mt-2 pt-2 border-top-1 surface-border">
                                                <Button
                                                    label="Download invoice"
                                                    icon="pi pi-download"
                                                    severity="info"
                                                    outlined
                                                    size="small"
                                                    loading={!!downloadingInvoices[inv.id]}
                                                    disabled={!!downloadingInvoices[inv.id]}
                                                    onClick={() => handleDownloadInvoice(inv)}
                                                />
                                            </div>
                                        </div>
                                    </AccordionTab>
                                ))}
                            </Accordion>
                            <Paginator
                                first={first}
                                rows={rows}
                                totalRecords={totalRecords}
                                onPageChange={(e) => { setFirst(e.first); setRows(e.rows); }}
                                template="PrevPageLink PageLinks NextPageLink CurrentPageReport"
                                currentPageReportTemplate="{first}-{last} of {totalRecords}"
                                pageLinkSize={3}
                                className="text-sm mt-2"
                            />
                        </>
                    )}
                </div>

                {/* Desktop (≥ md) — DataTable */}
                <div className="hidden md:block">
                    <DataTable
                        value={invoices}
                        dataKey="id"
                        lazy
                        loading={loading}
                        paginator
                        first={first}
                        rows={rows}
                        totalRecords={totalRecords}
                        onPage={(e) => { setFirst(e.first); setRows(e.rows); }}
                        sortField={sortField}
                        sortOrder={sortOrder}
                        onSort={(e) => { setSortField(e.sortField); setSortOrder(e.sortOrder); setFirst(0); }}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                        currentPageReportTemplate="Showing {first}-{last} of {totalRecords}"
                        paginatorClassName="text-sm"
                        pageLinkSize={3}
                        rowsPerPageOptions={[10, 25, 50]}
                        responsiveLayout="scroll"
                        scrollable
                        stripedRows
                        showGridlines
                        size="small"
                        className="p-datatable-gridlines"
                        emptyMessage={
                            <div className="flex flex-column align-items-center py-5">
                                <i className="pi pi-inbox pi-2x text-400 mb-3" aria-hidden="true" />
                                <span className="text-600">No invoices found</span>
                            </div>
                        }
                    >
                        <Column
                            field="invoice_number"
                            header="Invoice"
                            body={invoiceNumberBodyTemplate}
                            sortable
                            frozen
                            alignFrozen="left"
                            style={{ minWidth: '200px' }}
                        />
                        <Column
                            field="description"
                            header="Description"
                            body={descriptionBodyTemplate}
                            style={{ minWidth: '240px' }}
                        />
                        <Column
                            field="total_amount"
                            header="Amount"
                            body={amountBodyTemplate}
                            sortable
                            style={{ minWidth: '140px' }}
                        />
                        <Column
                            field="status"
                            header="Status"
                            body={statusBodyTemplate}
                            sortable
                            style={{ minWidth: '120px' }}
                        />
                        <Column
                            header="Actions"
                            body={actionsBodyTemplate}
                            frozen
                            alignFrozen="right"
                            style={{ minWidth: '80px', width: '80px' }}
                        />
                    </DataTable>
                </div>
                </Card>
            </div>
        </div>
    );
};

export default BillingHistory;
